import { PortalIdentifier, PortalName } from 'parcels/src/common/Portal';

export interface PortalTheme {
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor?: string;
  quaternaryColor?: string;
}

type PortalNameTheme = {
  [key in string]: PortalTheme;
};

export const portalNameThemeMap: PortalNameTheme = {
  [PortalName.UMR]: {
    primaryColor: '#002677',
    secondaryColor: '#196ECF',
  },
  [PortalName.UMR_PORTAL]: {
    primaryColor: '#002677',
    secondaryColor: '#196ECF',
  },
  [PortalIdentifier.GEHA]: {
    primaryColor: '#1C0950',
    secondaryColor: '#500778',
    tertiaryColor: '#E8E9F3',
  },
  [PortalIdentifier.GEHA_V2]: {
    primaryColor: '#00172f',
    secondaryColor: '#4c728a',
    tertiaryColor: '#CAD9E2',
    quaternaryColor: '#4c728a',
  },
  [PortalName.STUDENT_RESOURCE]: {
    primaryColor: '#002677',
    secondaryColor: '#196ECF',
  },
  [PortalName.ALL_SAVERS]: {
    primaryColor: '#002677',
    secondaryColor: '#196ECF',
  },
  [PortalName.UHONE]: {
    primaryColor: '#002677',
    secondaryColor: '#196ECF',
  },
};

// These overrides are copied from container app so we can test the white labeling standalone.
// The owner is really container app because both PSX and CEX should be using the same theme overrides.
// So if there's any update required, update here first to test standalone then update the one in container app.
// https://github.com/uhc-consumer/search-container/blob/main/products/web/src/style/theme.tsx
export const createThemeOverride = ({
  primaryColor,
  secondaryColor,
  tertiaryColor,
  quaternaryColor,
}: PortalTheme): object => {
  const primary1 = primaryColor ?? '#002677';
  const interactive1 = secondaryColor ?? '#196ECF';
  const interactive2 = quaternaryColor ?? '#004BA0';
  const interactive3 = tertiaryColor ?? '#D9E9FA';

  return {
    theme: {
      colors: {
        primary1,

        footerBackground: '$core.color.brand.100',
        carouselContentPrimary: '$core.color.brand.100',
        carouselButtonDefault: '$core.color.brand.100',
        loadingSpinnerPrimary1: '$core.color.brand.100',
        avatarContentAquaBorderColor: '$core.color.brand.100',
        avatarContentBlueBorderColor: '$core.color.brand.100',
        navMenuBackgroundDefault: '$core.color.brand.100',
        navMenuTextHover: '$core.color.brand.100',
        'core-color-brand-100': primary1,
        'semantic-color-surface-interactive-standards-rest-default-primary':
          '$core.color.brand.100',
        'semantic-color-surface-interactive-standards-active-selected-tertiary':
          '$core.color.brand.100',
        'semantic-color-surface-interactive-controls-rest-selected-pickers':
          '$core.color.brand.100',
        'semantic-color-surface-interactive-controls-active-selected-choice':
          '$core.color.brand.100',
        'semantic-color-surface-interactive-headerElement-rest-default':
          '$core.color.brand.100',
        'semantic-color-surface-container-primary': '$core.color.brand.100',
        'semantic-color-surface-container-header-topLevelNav':
          '$core.color.brand.100',
        'semantic-color-border-interactive-controls-active-selected':
          '$core.color.brand.100',
        'semantic-color-border-content-primary': '$core.color.brand.100',
        'semantic-color-icon-interactive-rest-primary': '$core.color.brand.100',
        'semantic-color-icon-interactive-active-tertiary':
          '$core.color.brand.100',
        'semantic-color-icon-content-primary': '$core.color.brand.100',
        'semantic-color-text-interactive-active-primary':
          '$core.color.brand.100',
        'semantic-color-text-label-cta-primary': '$core.color.brand.100',
        'semantic-color-text-content-primary': '$core.color.brand.100',
        'accordionV2-color-text-trigger': '$core.color.brand.100',
        'accordionV2-color-fill-chevron-default': '$core.color.brand.100',
        'accordionV2-color-fill-headerIcon': '$core.color.brand.100',
        'breadcrumbsV2-color-text-link-active': '$core.color.brand.100',
        'button-color-text-root-tertiary-default': '$core.color.brand.100',
        'buttonV2-color-text-root-secondary': '$core.color.brand.100',
        'buttonV2-color-text-root-tertiary-active': '$core.color.brand.100',
        'buttonV2-color-background-root-primary-default':
          '$core.color.brand.100',
        'buttonV2-color-border-root-secondary': '$core.color.brand.100',
        'buttonV2-color-fill-svg-secondary-default': '$core.color.brand.100',
        'buttonV2-color-fill-svg-tertiary-active': '$core.color.brand.100',
        'footerV2-color-background-root-default': '$core.color.brand.100',
        'formInputV2-color-background-root-inputType-checkbox-checked-active':
          '$core.color.brand.100',
        'linkV2-color-text-active': '$core.color.brand.100',
        'navMenu-color-background-link-inverted-hover': '$core.color.brand.100',
        'navMenu-color-background-menuTrigger-inverted-hover':
          '$core.color.brand.100',
        'navMenu-color-background-root-default': '$core.color.brand.100',
        'navMenu-color-background-scrollButton-default-default':
          '$core.color.brand.100',
        'navMenu-color-background-scrollButton-inverted-hover':
          '$core.color.brand.100',
        'navMenu-color-fill-columnTitleExternalIcon': '$core.color.brand.100',
        'navMenu-color-fill-menuTriggerIcon-default-open-default':
          '$core.color.brand.100',
        'navMenu-color-fill-menuTriggerIcon-default-open-hover':
          '$core.color.brand.100',
        'navMenu-color-fill-menuTriggerIcon-inverted-closed':
          '$core.color.brand.100',
        'navMenu-color-fill-scrollButtonIcon-default-default':
          '$core.color.brand.100',
        'navMenu-color-fill-scrollButtonIcon-inverted-default':
          '$core.color.brand.100',
        'navMenu-color-text-columnTitle-default': '$core.color.brand.100',
        'navMenu-color-text-link-inverted-default': '$core.color.brand.100',
        'navMenu-color-text-menuTrigger-default-hover': '$core.color.brand.100',
        'navMenu-color-text-menuTrigger-inverted-default':
          '$core.color.brand.100',
        'navMenuV2-color-background-default-default': '$core.color.brand.100',
        'navMenuV2-color-background-link-inverted-hover':
          '$core.color.brand.100',
        'navMenuV2-color-background-menuTrigger-inverted-hover':
          '$core.color.brand.100',
        'navMenuV2-color-background-root-default': '$core.color.brand.100',
        'navMenuV2-color-background-scrollButton-default-default':
          '$core.color.brand.100',
        'navMenuV2-color-background-scrollButton-inverted-hover':
          '$core.color.brand.100',
        'navMenuV2-color-fill-columnTitleExternalIcon': '$core.color.brand.100',
        'navMenuV2-color-fill-menuTriggerIcon-default-open-default':
          '$core.color.brand.100',
        'navMenuV2-color-fill-menuTriggerIcon-default-open-hover':
          '$core.color.brand.100',
        'navMenuV2-color-fill-menuTriggerIcon-inverted-closed':
          '$core.color.brand.100',
        'navMenuV2-color-fill-scrollButtonIcon-default-default':
          '$core.color.brand.100',
        'navMenuV2-color-fill-scrollButtonIcon-inverted-default':
          '$core.color.brand.100',
        'navMenuV2-color-text-columnTitle-default': '$core.color.brand.100',
        'navMenuV2-color-text-link-default-hover': '$core.color.brand.100',
        'navMenuV2-color-text-link-inverted-default': '$core.color.brand.100',
        'navMenuV2-color-text-menuTrigger-default-hover':
          '$core.color.brand.100',
        'navMenuV2-color-text-menuTrigger-inverted-default':
          '$core.color.brand.100',
        'pageBodyIntro-color-text-titleText': '$core.color.brand.100',
        'pageFooter-color-background-root': '$core.color.brand.100',
        // Overrides for tokens that use interactive1 color value
        interactive1,
        info1: '$core.color.brand.60',
        accent1: '$core.color.brand.60',
        toggleTabsSelected: '$core.color.brand.60',
        carouselContentSecondary: '$core.color.brand.60',
        'core-color-info1': interactive1,
        'core-color-brand-60': interactive1,
        'core-color-blue-80': interactive1,
        'semantic-color-surface-interactive-standards-rest-selected-tertiary':
          '$core.color.brand.60',
        'semantic-color-surface-interactive-controls-rest-selected-choice':
          '$core.color.brand.60',
        'semantic-color-surface-interactive-headerElement-rest-selected':
          '$core.color.brand.60',
        'semantic-color-surface-container-status-info-saturated':
          '$core.color.brand.60',
        'semantic-color-border-interactive-controls-rest-selected':
          '$core.color.brand.60',
        'semantic-color-border-interactive-stateAffordance-selected':
          '$core.color.brand.60',
        'semantic-color-border-interactive-forms-hover': '$core.color.brand.60',
        'semantic-color-border-status-info': '$core.color.brand.60',
        'semantic-color-icon-interactive-rest-tertiary': '$core.color.brand.60',
        'semantic-color-icon-status-info': '$core.color.brand.60',
        'semantic-color-text-interactive-rest-primary': '$core.color.brand.60',
        'semantic-color-text-status-info': '$core.color.brand.60',
        'alert-color-border-root-info': '$core.color.brand.60',
        'alert-color-border-actionButton-info': '$core.color.brand.60',
        'alert-color-icon-alertIcon-info': '$core.color.brand.60',
        'badge-color-text-container-info': '$core.color.brand.60',
        'badge-color-border-container-info': '$core.color.brand.60',
        'badgeV2-color-text-container-info': '$core.color.brand.60',
        'badgeV2-color-border-container-info': '$core.color.brand.60',
        'breadcrumbsV2-color-text-link-default': '$core.color.brand.60',
        'breadcrumbsV2-color-icon-mobile': '$core.color.brand.60',
        'buttonV2-color-text-root-tertiary-default': '$core.color.brand.60',
        'buttonV2-color-fill-svg-tertiary-default': '$core.color.brand.60',
        'checkboxV2-color-border-checked-true-rest': '$core.color.brand.60',
        'formInputV2-color-background-root-inputType-checkbox-checked-rest':
          '$core.color.brand.60',
        'formInputV2-color-border-root-default-hover': '$core.color.brand.60',
        'formInputV2-color-text-root-caret-default': '$core.color.brand.60',
        'formInputLabelV2-color-text-helperIcon': '$core.color.brand.60',
        'link-color-text-default': '$core.color.brand.60',
        'linkV2-color-text-default': '$core.color.brand.60',
        'navMenu-color-fill-itemLinkExternalIcon-default':
          '$core.color.brand.60',
        'navMenu-color-fill-routerHeaderIcon': '$core.color.brand.60',
        'navMenu-color-focus-root': '$core.color.brand.60',
        'navMenu-color-focus-drawerMenuItem': '$core.color.brand.60',
        'navMenu-color-text-drawerMenuItem': '$core.color.brand.60',
        'navMenu-color-text-itemLinkTitle-default': '$core.color.brand.60',
        'navMenuV2-color-fill-itemLinkExternalIcon-default':
          '$core.color.brand.60',
        'navMenuV2-color-fill-routerHeaderIcon': '$core.color.brand.60',
        'navMenuV2-color-focus-root': '$core.color.brand.60',
        'navMenuV2-color-focus-drawerMenuItem': '$core.color.brand.60',
        'navMenuV2-color-text-drawerMenuItem': '$core.color.brand.60',
        'navMenuV2-color-text-itemLinkTitle-default': '$core.color.brand.60',
        'pageBodyIntro-color-border-link-active': '$core.color.brand.60',
        'pageBodyIntro-color-text-profileHeadingText': '$core.color.brand.60',
        'selectInput-color-border-input-open': '$core.color.brand.60',
        'selectInput-color-border-optionListContainer-open':
          '$core.color.brand.60',
        'selectInput-color-fill-icon-default': '$core.color.brand.60',
        'selectInput-color-fill-icon-hover': '$core.color.brand.60',
        'stepIndicator-color-background-stepBar-active': '$core.color.brand.60',
        'stepIndicator-color-fill-scrollButton': '$core.color.brand.60',
        'stepIndicator-color-outline-stepIcon': '$core.color.brand.60',
        'tabs-color-border-activeLine': '$core.color.brand.60',
        'tabs-color-focusRing-tab': '$core.color.brand.60',
        'tabs-color-text-tab-default': '$core.color.brand.60',
        'toast-color-background-container-info': '$core.color.brand.60',
        // Overrides for tokens that use interactive2 color value
        interactive2,
        'core-color-brand-80': interactive2,
        // Overrides for tokens that use interactive3 color value
        interactive3,
      },
    },
  };
};
